import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import termsMarkdown from "../../content/legal/ventahub-terms-of-service.md?raw";
import privacyMarkdown from "../../content/legal/ventahub-privacy-policy.md?raw";
import useDocumentTitle from "../../hooks/useDocumentTitle";

const DOCS = {
  terms: {
    markdown: termsMarkdown,
    title: "Terms of Service",
    otherPath: "/privacy",
    otherLabel: "Privacy Policy",
  },
  privacy: {
    markdown: privacyMarkdown,
    title: "Privacy Policy",
    otherPath: "/terms",
    otherLabel: "Terms of Service",
  },
};

const getLastUpdated = (markdown) => {
  const match = markdown.match(/\*\*Last Updated:?\*\*\s*(.+)/i);
  return match ? match[1].trim() : null;
};

const markdownComponents = {
  h1: ({ children }) => (
    <h1 className="text-2xl font-bold text-gray-900 mt-8 mb-4 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 border-b border-gray-200 pb-1">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-semibold text-gray-900 mt-6 mb-2">{children}</h3>
  ),
  p: ({ children }) => <p className="text-sm leading-6 text-gray-700 mb-4">{children}</p>,
  ul: ({ children }) => (
    <ul className="list-disc pl-5 text-sm leading-6 text-gray-700 mb-4 space-y-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 text-sm leading-6 text-gray-700 mb-4 space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  a: ({ children, href }) => (
    <a href={href} className="text-teal-600 hover:text-teal-800 underline" target="_blank" rel="noreferrer">
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto mb-4 border border-gray-200 rounded-md">
      <table className="min-w-full divide-y divide-gray-200 text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
  th: ({ children }) => (
    <th className="px-3 py-2 text-left font-semibold text-gray-900 whitespace-nowrap">{children}</th>
  ),
  td: ({ children }) => <td className="px-3 py-2 text-gray-700 align-top">{children}</td>,
  tr: ({ children }) => <tr className="even:bg-gray-50">{children}</tr>,
};

const LegalPage = ({ doc }) => {
  const config = DOCS[doc];
  const lastUpdated = getLastUpdated(config.markdown);

  useDocumentTitle(config.title);

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-4">
      <div className="max-w-prose mx-auto bg-white rounded shadow p-6 sm:p-10">
        <div className="mb-6">
          <Link to="/login" className="text-sm text-teal-600 hover:text-teal-800">
            &larr; Back to login
          </Link>
        </div>

        {lastUpdated && (
          <div className="mb-4 inline-block rounded-full bg-teal-50 text-teal-800 text-xs font-medium px-3 py-1">
            Last Updated: {lastUpdated}
          </div>
        )}

        <article>
          <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
            {config.markdown}
          </ReactMarkdown>
        </article>

        <div className="mt-8 pt-6 border-t border-gray-200 text-sm">
          Also read our{" "}
          <Link to={config.otherPath} className="text-teal-600 hover:text-teal-800 underline">
            {config.otherLabel}
          </Link>
          .
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
